import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import api from '../../utils/api';
import { getImageUrl } from '../../utils/helpers';
import { 
  Plus, Edit, Trash2, Search, Star, Upload, Image, X, Download, 
  Filter, User, Phone, Mail, Building2, MapPin, DollarSign, ArrowRight,
  Settings, Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const HOTEL_TYPES = ['Hotel', 'Resort', 'Villa', 'Cottage', 'Homestay', 'Apartment'];

const HotelManager = () => {
  const navigate = useNavigate();
  const { buildPath } = useTenant();
  const [hotels, setHotels] = useState([]);
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingHotel, setEditingHotel] = useState(null);
  const [editingContact, setEditingContact] = useState(null);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [hotelPhotos, setHotelPhotos] = useState([]);
  const [hotelContacts, setHotelContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const fileInputRef = useRef(null);
  const hotelExcelInputRef = useRef(null);
  
  const [filters, setFilters] = useState({ place_id: '', star_rating: '' });
  
  const [hotelForm, setHotelForm] = useState({
    place_id: '', hotel_name: '', star_rating: '', address: '', hotel_type: 'Hotel', description: '',
    weekend_monday: false, weekend_tuesday: false, weekend_wednesday: false, weekend_thursday: false,
    weekend_friday: false, weekend_saturday: false, weekend_sunday: false
  });

  const DAYS_OF_WEEK = [
    { key: 'weekend_monday', label: 'Monday', short: 'Mon' },
    { key: 'weekend_tuesday', label: 'Tuesday', short: 'Tue' },
    { key: 'weekend_wednesday', label: 'Wednesday', short: 'Wed' },
    { key: 'weekend_thursday', label: 'Thursday', short: 'Thu' },
    { key: 'weekend_friday', label: 'Friday', short: 'Fri' },
    { key: 'weekend_saturday', label: 'Saturday', short: 'Sat' },
    { key: 'weekend_sunday', label: 'Sunday', short: 'Sun' },
  ];

  const [contactForm, setContactForm] = useState({
    contact_name: '', contact_email: '', contact_phone: '', designation: '', is_primary: false,
  });

  useEffect(() => { fetchHotels(); fetchPlaces(); }, []);

  const fetchHotels = async () => {
    try {
      setLoading(true);
      let url = '/hotels';
      const params = new URLSearchParams();
      if (filters.place_id) params.append('place_id', filters.place_id);
      if (params.toString()) url += `?${params.toString()}`;
      const response = await api.get(url);
      setHotels(response.data);
    } catch (error) {
      toast.error('Failed to load hotels');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlaces = async () => {
    try { const response = await api.get('/places'); setPlaces(response.data); } catch (error) { console.error('Failed to load places'); }
  };

  const fetchHotelPhotos = async (hotelId) => {
    try { const response = await api.get(`/hotels/${hotelId}/photos`); setHotelPhotos(response.data); } 
    catch (error) { console.error('Failed to load photos'); setHotelPhotos([]); }
  };

  const fetchHotelContacts = async (hotelId) => {
    try { const response = await api.get(`/hotels/${hotelId}/contacts`); setHotelContacts(response.data); } 
    catch (error) { console.error('Failed to load contacts'); setHotelContacts([]); }
  };

  const handleSelectHotel = async (hotel) => {
    setSelectedHotel(hotel);
    fetchHotelPhotos(hotel.hotel_id);
    fetchHotelContacts(hotel.hotel_id);
  };

  const resetHotelForm = () => {
    setHotelForm({ 
      place_id: '', hotel_name: '', star_rating: '', address: '', hotel_type: 'Hotel', description: '',
      weekend_monday: false, weekend_tuesday: false, weekend_wednesday: false, weekend_thursday: false,
      weekend_friday: false, weekend_saturday: false, weekend_sunday: false
    });
    setEditingHotel(null);
  };

  const handleEditHotel = (hotel) => {
    setHotelForm({
      place_id: hotel.place_id || '', hotel_name: hotel.hotel_name, star_rating: hotel.star_rating?.toString() || '',
      address: hotel.address || '', hotel_type: hotel.hotel_type || 'Hotel', description: hotel.description || '',
      weekend_monday: hotel.weekend_monday || false,
      weekend_tuesday: hotel.weekend_tuesday || false,
      weekend_wednesday: hotel.weekend_wednesday || false,
      weekend_thursday: hotel.weekend_thursday || false,
      weekend_friday: hotel.weekend_friday || false,
      weekend_saturday: hotel.weekend_saturday === true,
      weekend_sunday: hotel.weekend_sunday === true,
    });
    setEditingHotel(hotel);
    setDialogOpen(true);
  };

  const handleHotelSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...hotelForm, star_rating: hotelForm.star_rating ? parseInt(hotelForm.star_rating) : null };
      if (editingHotel) {
        await api.put(`/hotels/${editingHotel.hotel_id}`, data);
        toast.success('Hotel updated');
      } else {
        await api.post('/hotels', data);
        toast.success('Hotel created');
      }
      setDialogOpen(false);
      resetHotelForm();
      fetchHotels();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save hotel');
    }
  };

  const handleDeleteHotel = async (hotelId) => {
    if (!confirm('Delete this hotel?')) return;
    try {
      await api.delete(`/hotels/${hotelId}`);
      toast.success('Hotel deleted');
      if (selectedHotel?.hotel_id === hotelId) setSelectedHotel(null);
      fetchHotels();
    } catch (error) {
      toast.error('Failed to delete hotel');
    }
  };

  // Photo handling
  const handlePhotoUpload = async (e) => {
    if (!selectedHotel || !e.target.files?.length) return;
    const formData = new FormData();
    formData.append('files', e.target.files[0]);
    try {
      await api.post(`/upload/hotel/${selectedHotel.hotel_id}/photos`, formData);
      toast.success('Photo uploaded');
      fetchHotelPhotos(selectedHotel.hotel_id);
    } catch (error) {
      toast.error('Failed to upload photo');
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await api.delete(`/hotels/${selectedHotel.hotel_id}/photos/${photoId}`);
      toast.success('Photo deleted');
      fetchHotelPhotos(selectedHotel.hotel_id);
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  // Contact handling
  const resetContactForm = () => {
    setContactForm({ contact_name: '', contact_email: '', contact_phone: '', designation: '', is_primary: false });
    setEditingContact(null);
  };

  const handleEditContact = (contact) => {
    setContactForm({
      contact_name: contact.contact_name || '', contact_email: contact.contact_email || '',
      contact_phone: contact.contact_phone || '', designation: contact.designation || '', is_primary: contact.is_primary || false,
    });
    setEditingContact(contact);
    setContactDialogOpen(true);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingContact) {
        await api.put(`/hotels/${selectedHotel.hotel_id}/contacts/${editingContact.contact_id}`, contactForm);
        toast.success('Contact updated');
      } else {
        await api.post(`/hotels/${selectedHotel.hotel_id}/contacts`, contactForm);
        toast.success('Contact added');
      }
      setContactDialogOpen(false);
      resetContactForm();
      fetchHotelContacts(selectedHotel.hotel_id);
    } catch (error) {
      toast.error('Failed to save contact');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!confirm('Delete this contact?')) return;
    try {
      await api.delete(`/hotels/${selectedHotel.hotel_id}/contacts/${contactId}`);
      toast.success('Contact deleted');
      fetchHotelContacts(selectedHotel.hotel_id);
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  // Excel handling
  const downloadHotelTemplate = async () => {
    try {
      const response = await api.get('/admin/hotels/template', {
        responseType: 'blob'
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'hotels_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleHotelExcelUpload = async (e) => {
    if (!e.target.files?.length) return;
    const formData = new FormData();
    formData.append('file', e.target.files[0]);
    try {
      const response = await api.post('/admin/import/hotels', formData);
      toast.success(response.data.message || 'Hotels imported');
      fetchHotels();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    }
  };

  const getPlaceName = (placeId) => places.find(p => p.place_id === placeId)?.place_name || 'Unknown';

  const filteredHotels = hotels.filter(h => 
    h.hotel_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getPlaceName(h.place_id)?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Navigate to rate manager
  const openRateManager = (hotel) => {
    navigate(buildPath(`/admin/hotel-rates?hotel=${hotel.hotel_id}`));
  };

  return (
    <Layout>
      <div className="space-y-4" data-testid="hotel-manager">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Hotels</h1>
            <p className="text-sm text-gray-500">Manage hotel information, photos, and contacts</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={downloadHotelTemplate}>
              <Download className="w-4 h-4 mr-1" />Template
            </Button>
            <Button variant="outline" size="sm" onClick={() => hotelExcelInputRef.current?.click()}>
              <Upload className="w-4 h-4 mr-1" />Import
            </Button>
            <input type="file" ref={hotelExcelInputRef} onChange={handleHotelExcelUpload} accept=".xlsx,.xls,.csv" className="hidden" />
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" onClick={resetHotelForm}>
                  <Plus className="w-4 h-4 mr-1" />Add Hotel
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingHotel ? 'Edit Hotel' : 'Add Hotel'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleHotelSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Hotel Name *</Label>
                      <Input value={hotelForm.hotel_name} onChange={(e) => setHotelForm({ ...hotelForm, hotel_name: e.target.value })} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Location *</Label>
                      <SearchableSelect
                        options={places.map(p => ({ value: p.place_id, label: p.place_name }))}
                        value={hotelForm.place_id}
                        onValueChange={(v) => setHotelForm({ ...hotelForm, place_id: v })}
                        placeholder="Select place"
                        searchPlaceholder="Search places..."
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Star Rating</Label>
                      <Select value={hotelForm.star_rating} onValueChange={(v) => setHotelForm({ ...hotelForm, star_rating: v })}>
                        <SelectTrigger><SelectValue placeholder="Select rating" /></SelectTrigger>
                        <SelectContent>{[1, 2, 3, 4, 5].map((r) => <SelectItem key={r} value={r.toString()}>{r} Star</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Hotel Type</Label>
                      <Select value={hotelForm.hotel_type} onValueChange={(v) => setHotelForm({ ...hotelForm, hotel_type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{HOTEL_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Address</Label>
                    <Input value={hotelForm.address} onChange={(e) => setHotelForm({ ...hotelForm, address: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea value={hotelForm.description} onChange={(e) => setHotelForm({ ...hotelForm, description: e.target.value })} rows={2} />
                  </div>
                  
                  {/* Weekend Days Configuration */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      Weekend Days
                      <span className="text-xs text-gray-500 font-normal">(for rate pricing)</span>
                    </Label>
                    <div className="grid grid-cols-7 gap-1">
                      {DAYS_OF_WEEK.map((day) => (
                        <div 
                          key={day.key}
                          onClick={() => setHotelForm({ ...hotelForm, [day.key]: !hotelForm[day.key] })}
                          className={`
                            flex flex-col items-center justify-center p-2 rounded-lg cursor-pointer
                            transition-all border text-xs
                            ${hotelForm[day.key] 
                              ? 'bg-orange-100 border-orange-400 text-orange-800' 
                              : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'}
                          `}
                        >
                          <span className="font-medium">{day.short}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Cancel</Button>
                    <Button type="submit" className="flex-1">{editingHotel ? 'Update' : 'Create'}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search hotels..." className="pl-9" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
            <Filter className="w-4 h-4 mr-1" />Filters
          </Button>
        </div>

        {showFilters && (
          <Card>
            <CardContent className="pt-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs">Location</Label>
                  <Select value={filters.place_id} onValueChange={(v) => setFilters({ ...filters, place_id: v })}>
                    <SelectTrigger className="h-9"><SelectValue placeholder="All locations" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All locations</SelectItem>
                      {places.map((p) => <SelectItem key={p.place_id} value={p.place_id}>{p.place_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button variant="outline" size="sm" onClick={() => { setFilters({ place_id: '', star_rating: '' }); fetchHotels(); }}>Reset</Button>
                  <Button size="sm" className="ml-2" onClick={fetchHotels}>Apply</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Hotel List */}
          <div className="lg:col-span-1 space-y-2">
            <div className="text-sm text-gray-500">{filteredHotels.length} hotels found</div>
            <div className="space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto">
              {filteredHotels.map((hotel) => (
                <Card 
                  key={hotel.hotel_id} 
                  className={`cursor-pointer transition-colors hover:bg-gray-50 ${selectedHotel?.hotel_id === hotel.hotel_id ? 'ring-2 ring-blue-500' : ''}`}
                  onClick={() => handleSelectHotel(hotel)}
                  data-testid={`hotel-card-${hotel.hotel_id}`}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium text-sm">{hotel.hotel_name}</h3>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                          <MapPin className="w-3 h-3" />
                          <span>{getPlaceName(hotel.place_id)}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {hotel.star_rating && (
                            <Badge variant="secondary" className="text-xs py-0">
                              {hotel.star_rating} <Star className="w-2.5 h-2.5 ml-0.5 fill-yellow-400 text-yellow-400" />
                            </Badge>
                          )}
                          {hotel.hotel_type && (
                            <Badge variant="outline" className="text-xs py-0">{hotel.hotel_type}</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); handleEditHotel(hotel); }}>
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); handleDeleteHotel(hotel.hotel_id); }}>
                          <Trash2 className="w-3 h-3 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {filteredHotels.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Building2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No hotels found</p>
                </div>
              )}
            </div>
          </div>

          {/* Hotel Details */}
          <div className="lg:col-span-2">
            {selectedHotel ? (
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{selectedHotel.hotel_name}</CardTitle>
                      <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                        <MapPin className="w-4 h-4" />
                        <span>{getPlaceName(selectedHotel.place_id)}</span>
                        {selectedHotel.star_rating && (
                          <>
                            <span>•</span>
                            <span>{selectedHotel.star_rating} Star</span>
                          </>
                        )}
                      </div>
                    </div>
                    <Button onClick={() => openRateManager(selectedHotel)} className="bg-blue-600 hover:bg-blue-700">
                      <DollarSign className="w-4 h-4 mr-2" />
                      Manage Rates
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Description */}
                  {selectedHotel.description && (
                    <div>
                      <h4 className="text-sm font-medium mb-1">Description</h4>
                      <p className="text-sm text-gray-600">{selectedHotel.description}</p>
                    </div>
                  )}

                  {/* Address */}
                  {selectedHotel.address && (
                    <div>
                      <h4 className="text-sm font-medium mb-1">Address</h4>
                      <p className="text-sm text-gray-600">{selectedHotel.address}</p>
                    </div>
                  )}

                  {/* Photos */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium" data-testid="hotel-photo-count">Photos ({hotelPhotos.length})</h4>
                      <input type="file" ref={fileInputRef} onChange={handlePhotoUpload} accept="image/*" className="hidden" data-testid="hotel-photo-input" />
                      <Button size="sm" variant="outline" data-testid="hotel-add-photo-btn" onClick={() => fileInputRef.current?.click()}>
                        <Image className="w-3 h-3 mr-1" />Add Photo
                      </Button>
                    </div>
                    {hotelPhotos.length > 0 ? (
                      <div className="flex gap-2 flex-wrap">
                        {hotelPhotos.map((photo) => (
                          <div key={photo.photo_id} className="relative group" data-testid={`hotel-photo-${photo.photo_id}`}>
                            <img src={getImageUrl(photo.photo_url)} alt="" className="w-20 h-20 object-cover rounded-lg" />
                            <Button 
                              variant="destructive" 
                              size="sm" 
                              className="absolute -top-2 -right-2 h-5 w-5 p-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => handleDeletePhoto(photo.photo_id)}
                              data-testid={`hotel-photo-delete-${photo.photo_id}`}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">No photos uploaded</p>
                    )}
                  </div>

                  {/* Contacts */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium">Contacts ({hotelContacts.length})</h4>
                      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="outline" onClick={resetContactForm}>
                            <User className="w-3 h-3 mr-1" />Add Contact
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>{editingContact ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
                          </DialogHeader>
                          <form onSubmit={handleContactSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label>Name *</Label>
                                <Input value={contactForm.contact_name} onChange={(e) => setContactForm({ ...contactForm, contact_name: e.target.value })} required />
                              </div>
                              <div className="space-y-2">
                                <Label>Designation</Label>
                                <Input value={contactForm.designation} onChange={(e) => setContactForm({ ...contactForm, designation: e.target.value })} placeholder="Manager" />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label>Email</Label>
                                <Input type="email" value={contactForm.contact_email} onChange={(e) => setContactForm({ ...contactForm, contact_email: e.target.value })} />
                              </div>
                              <div className="space-y-2">
                                <Label>Phone</Label>
                                <Input value={contactForm.contact_phone} onChange={(e) => setContactForm({ ...contactForm, contact_phone: e.target.value })} />
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button type="button" variant="outline" onClick={() => setContactDialogOpen(false)} className="flex-1">Cancel</Button>
                              <Button type="submit" className="flex-1">{editingContact ? 'Update' : 'Add'}</Button>
                            </div>
                          </form>
                        </DialogContent>
                      </Dialog>
                    </div>
                    {hotelContacts.length > 0 ? (
                      <div className="space-y-2">
                        {hotelContacts.map((contact) => (
                          <div key={contact.contact_id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-blue-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium">{contact.contact_name}</p>
                                <div className="flex items-center gap-3 text-xs text-gray-500">
                                  {contact.designation && <span>{contact.designation}</span>}
                                  {contact.contact_phone && (
                                    <span className="flex items-center gap-1">
                                      <Phone className="w-3 h-3" />{contact.contact_phone}
                                    </span>
                                  )}
                                  {contact.contact_email && (
                                    <span className="flex items-center gap-1">
                                      <Mail className="w-3 h-3" />{contact.contact_email}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleEditContact(contact)}>
                                <Edit className="w-3 h-3" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDeleteContact(contact.contact_id)}>
                                <Trash2 className="w-3 h-3 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">No contacts added</p>
                    )}
                  </div>

                  {/* Quick Actions */}
                  <div className="pt-4 border-t">
                    <h4 className="text-sm font-medium mb-3">Quick Actions</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <Button variant="outline" className="justify-start" onClick={() => openRateManager(selectedHotel)}>
                        <DollarSign className="w-4 h-4 mr-2" />
                        Manage Rates & Seasons
                      </Button>
                      <Button variant="outline" className="justify-start" onClick={() => openRateManager(selectedHotel)}>
                        <Settings className="w-4 h-4 mr-2" />
                        Room Configurations
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-16 text-gray-500">
                  <Building2 className="w-16 h-16 mb-4 opacity-30" />
                  <p className="text-lg font-medium">Select a hotel</p>
                  <p className="text-sm">Choose a hotel from the list to view details and manage rates</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default HotelManager;
